import { VEvent, Attendee, AttendeePartStat } from 'node-ical'

type ParameterValue = string | { val: string }
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18n'
// CalendarService(barrel) 순환 참조 방지를 위해 원본 모듈에서 직접 import
import { getUserInfo } from './CalendarState'
import { RRule } from 'rrule'
import { TodoItem } from 'pages/todo/modules/TodoItem'

class EventInstance {
  public readonly id: string
  public readonly etag: string
  public readonly serverId: string
  public readonly color: string
  public readonly folderColor: string
  public readonly event: VEvent | null
  public readonly todo: TodoItem | null
  public readonly exception: VEvent | null
  public readonly startDate: Date
  public readonly startTz: string
  public readonly endDate: Date
  public readonly endTz: string
  public readonly author: { val: string; params: { CN: string } } | null
  private mAttendees: Attendee[]
  private mAlarms: number[] // seconds
  private mIsMyEvent: boolean = false
  private mIsContainMe: boolean = false
  private mOrganizer: string
  private mOrganizerName: string
  private mMyMeetingResponse: AttendeePartStat
  private mIsRSVP: boolean
  private mAllday: boolean
  private mSubject: string
  private mLocation: string
  private mDescription: string
  private mIsBusy: boolean
  private mIsPrivate: boolean
  private mIsCancelled: boolean
  private mAttachments: any[] | null

  constructor(
    serverId: string,
    etag: string,
    color: string,
    event: VEvent | null = null,
    startDate: Date | null = null,
    exception: VEvent | null = null,
    todo: TodoItem | null = null,
  ) {
    this.id = uuidv4()
    this.etag = etag
    this.serverId = serverId
    this.event = event
    this.todo = todo
    this.exception = exception
    this.author =
      (exception?.['NI-AUTHOR'] as { val: string; params: { CN: string } }) ||
      (event?.['NI-AUTHOR'] as { val: string; params: { CN: string } }) ||
      null

    this.folderColor = color
    if (exception && 'color' in exception) {
      this.color = exception.color as string
    } else if (event && 'color' in event) {
      this.color = event.color as string
    } else if (todo && 'color' in todo) {
      this.color = todo.color as string
    } else {
      this.color = color
    }

    if (startDate != null) {
      if (exception != null) {
        this.startDate = exception.start
        this.startTz = exception.start.tz
        this.endDate = exception.end
        this.endTz = exception.end.tz
      } else {
        this.startDate = startDate
        const safeEnd = EventInstance._safeEndDate(event, event.start)
        const duration = safeEnd.getTime() - event.start.getTime()
        this.endDate = new Date(startDate.getTime() + duration)
        this.startTz = event.start.tz
        this.endTz = EventInstance._safeEndTz(event)
      }
    } else {
      if (event) {
        this.startDate = event.start
        this.endDate = EventInstance._safeEndDate(event, event.start)
        this.startTz = event.start.tz
        this.endTz = EventInstance._safeEndTz(event)
      } else if (todo) {
        this.startDate = todo.dueDate
        const endDate = new Date(todo.dueDate)
        endDate.setHours(23, 59, 59, 999)
        this.endDate = endDate
        this.startTz = this.endTz = Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    }

    this.mMyMeetingResponse = null

    this.parseProperties()
  }

  /**
   * event.end가 없을 때 RFC 5545 규칙에 따라 endDate를 계산한다.
   * VALUE=DATE(종일): start + 1일, DATE-TIME: start와 동일(0 duration)
   */
  private static _safeEndDate(event: VEvent, startDate: Date): Date {
    if (event.end) return event.end
    if (event.datetype === 'date') {
      // 종일 Date 는 로컬 자정 규약이므로 고정 24시간이 아니라 캘린더-일 단위로 더한다.
      // DST 해제일처럼 로컬 하루가 24시간이 아닌 경우에도 항상 다음날 로컬 자정이 된다.
      const nextDay = new Date(startDate)
      nextDay.setDate(nextDay.getDate() + 1)
      return nextDay
    }
    return new Date(startDate.getTime())
  }

  private static _safeEndTz(event: VEvent): string {
    return event.end?.tz ?? event.start?.tz ?? ''
  }

  changeFolderColor(color: string) {
    // 폴더의 색상 변경시 전체를 다시 만들기 보다는 folderColor만 수정.
    ;(this as any).folderColor = color
  }

  private parseOrganizer() {
    if (this.todo) {
      return
    }

    const organizer =
      this.exception && this.exception.organizer ? this.exception.organizer : this.event.organizer
    let organizerValue = null
    let organizerName = null
    if (organizer && typeof organizer === 'object' && 'val' in organizer) {
      organizerValue = organizer.val
      organizerName = organizer.params.CN
    } else if (typeof organizer === 'string') {
      organizerValue = organizer
    }
    if (organizerValue) {
      if (organizerValue.toLowerCase().startsWith('mailto:')) {
        this.mOrganizer = organizerValue.substring(7)
      } else {
        this.mOrganizer = organizerValue
      }
    }
    if (organizerName) {
      this.mOrganizerName = organizerName
    }
  }

  private parseAttendees() {
    if (this.todo) {
      return
    }

    let attendees: Attendee[] | Attendee = null
    if (this.exception) {
      attendees = this.exception.attendee ?? null
    } else {
      attendees = this.event?.attendee ?? null
    }

    if (attendees == null) {
      this.mAttendees = []
    } else if (!Array.isArray(attendees)) {
      this.mAttendees = [attendees]
    } else {
      this.mAttendees = attendees
    }

    if (this.mAttendees.length === 0) {
      return
    }

    let hasOrganizer = false
    this.mAttendees = this.mAttendees.map((attendee) => {
      let email = null
      if (typeof attendee === 'object' && 'val' in attendee) {
        email = attendee.val
        if (email.toLowerCase().startsWith('mailto:')) {
          email = email.substring(7)
        }
        if (email.toLowerCase() === this.mOrganizer?.toLowerCase()) {
          hasOrganizer = true
          attendee.params.ROLE = 'ORGANIZER'
        }
        return {
          ...attendee,
          val: email,
        }
      } else if (typeof attendee === 'string') {
        // 예외 케이스에 대한 처리
        let email = attendee
        if (email.toLowerCase().startsWith('mailto:')) {
          email = email.substring(7)
        }
        return {
          val: email,
          params: {},
        }
      } else {
        return {
          val: '',
          params: {},
        }
      }
    })
    if (!hasOrganizer && this.mAttendees.length > 0) {
      this.mAttendees.unshift({
        params: {
          RSVP: true,
          PARTSTAT: 'ACCEPTED',
          ROLE: 'ORGANIZER',
          CN: this.mOrganizerName ?? this.mOrganizer,
        },
        val: this.mOrganizer,
      })
    }
  }

  private parseAlarms() {
    if (this.todo) {
      return
    }

    const target = this.exception ? this.exception : this.event
    let alarms: number[] = []
    target.alarms?.forEach((alarm) => {
      if (alarm.type === 'VALARM' && alarm.action === 'DISPLAY') {
        const trigger = alarm.trigger
        let seconds = 0
        if (trigger) {
          seconds = this.secondsDuration(trigger)
        }
        alarms.push(seconds)
      }
    })
    this.mAlarms = alarms.sort((a, b) => b - a)
  }

  private secondsDuration(durStr: string): number {
    let seconds = 0
    if (durStr.length > 0) {
      let minus = true
      const sign = durStr.substring(0, 1)
      if (sign !== '-') {
        minus = false
      }
      let min = 0
      let digitStr = ''
      for (let index = 0; index < durStr.length; index++) {
        const ch = durStr.charAt(index)
        if (ch.trim().length === 0 || !isNaN(parseInt(ch))) {
          digitStr += ch
        } else if (digitStr.length > 0) {
          const digit = parseInt(digitStr)
          digitStr = ''
          if (ch === 'D') {
            min += 60 * 24 * digit
          } else if (ch === 'H') {
            min += 60 * digit
          } else if (ch === 'M') {
            min += digit
          }
        }
      }
      min *= minus ? -1 : 1
      seconds = min * 60
    }
    return seconds
  }

  get isMeeting(): boolean {
    if (this.todo) {
      return false
    }

    return this.mAttendees.length > 0
  }

  private parseIsMyEvent() {
    if (this.todo) {
      return
    }

    if (!this.isMeeting || this.mOrganizer == null) {
      this.mIsMyEvent = true
      return
    }

    const myEmail = getUserInfo()?.email
    if (this.mOrganizer.toLowerCase() === myEmail?.toLowerCase()) {
      this.mIsMyEvent = true
    }

    if (myEmail) {
      const normalizeEmail = (value: string) =>
        value
          .trim()
          .replace(/^mailto:/i, '')
          .trim()
          .toLowerCase()
      const normalizedMyEmail = normalizeEmail(myEmail)
      const attendee = this.mAttendees.find(
        (attendee) =>
          typeof attendee === 'object' &&
          'val' in attendee &&
          normalizeEmail(attendee.val) === normalizedMyEmail,
      )
      if (attendee) {
        this.mIsContainMe = true

        if (typeof attendee === 'object' && 'params' in attendee) {
          this.mIsRSVP = attendee.params.RSVP
          this.mMyMeetingResponse = ['ACCEPTED', 'DECLINED', 'TENTATIVE'].includes(
            attendee.params.PARTSTAT ?? '',
          )
            ? attendee.params.PARTSTAT
            : null
        }
      }
    }
  }

  private parseAttachments() {
    if (this.todo) {
      return
    }

    const target = this.event
    let attachments = target['attach'] as
      | Array<{ val: string; params: Record<string, string> }>
      | undefined
    if (attachments) {
      if (!Array.isArray(attachments)) {
        attachments = [attachments]
      }
    }
    if (!attachments || attachments.length === 0) {
      return
    }

    this.mAttachments = []
    attachments.forEach((attachment) => {
      if (typeof attachment !== 'object' || attachment.params == null || attachment.val == null) {
        return
      }

      let name = null
      let size = null
      let mimeType = null
      const url = attachment.val

      Object.keys(attachment.params).forEach((key) => {
        const upKey = key.toUpperCase()
        if (upKey === 'FILENAME' || upKey === 'X-APPLE-FILENAME') {
          name = attachment.params[key]
          name = decodeURIComponent(name)
        } else if (upKey === 'SIZE') {
          size = attachment.params[key]
        } else if (upKey === 'FMTTYPE') {
          mimeType = attachment.params[key]
        }
      })
      this.mAttachments.push({ filename: name, size, mimeType, url, attachment })
    })
  }

  private parseProperties() {
    this.parseOrganizer()
    this.parseAttendees()
    this.parseIsMyEvent()
    this.parseAlarms()
    this.parseAttachments()

    if (this.todo) {
      this.mAllday = true
      this.mSubject = this.todo.summary
      this.mLocation = this.todo.location
      this.mDescription = this.todo.description
      this.mIsBusy = false
      this.mIsPrivate = false
      this.mIsCancelled = this.todo.status === 'COMPLETED'
    } else {
      const target = this.exception ? this.exception : this.event
      this.mAllday = target.datetype === 'date'
      this.mSubject = paramToString(target.summary)
      this.mLocation = paramToString(target.location)
      this.mDescription = paramToString(target.description)
      this.mIsBusy = target.transparency !== 'TRANSPARENT'
      this.mIsPrivate = target.class === 'PRIVATE'
      this.mIsCancelled = target.status === 'CANCELLED'
    }
  }

  get organizer(): string {
    if (this.todo) {
      return ''
    }
    return this.mOrganizer
  }

  get organizerName(): string {
    if (this.todo) {
      return ''
    }
    return this.mOrganizerName
  }

  get allday(): boolean {
    return this.mAllday
  }

  get subject(): string {
    return this.mSubject
  }

  get location(): string | undefined {
    return this.mLocation
  }

  get description(): string | undefined {
    return this.mDescription
  }

  get isBusy(): boolean {
    return this.mIsBusy
  }

  get isPrivate(): boolean {
    return this.mIsPrivate
  }

  get attendees(): Attendee[] {
    return this.mAttendees
  }

  get alarms(): number[] {
    return this.mAlarms
  }

  get alarmsStrings(): string[] {
    return this.mAlarms.map((alarm) => {
      if (alarm === 0) {
        return i18n.t('calendar.alarm.atTimeOfEvent')
      }

      const isBefore = alarm < 0
      const seconds = Math.abs(alarm)
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const sec = seconds % 60
      // const { t } = useTranslation()

      let result = ''
      if (days > 0) {
        result += `${days} ${days > 1 ? i18n.t('calendar.time.days') : i18n.t('calendar.time.day')} `
      }
      if (hours > 0) {
        result += `${hours} ${hours > 1 ? i18n.t('calendar.time.hours') : i18n.t('calendar.time.hour')} `
      }
      if (minutes > 0) {
        result += `${minutes} ${minutes > 1 ? i18n.t('calendar.time.minutes') : i18n.t('calendar.time.minute')} `
      }
      if (sec > 0) {
        result += `${sec} ${sec > 1 ? i18n.t('calendar.time.seconds') : i18n.t('calendar.time.second')} `
      }
      if (isBefore) {
        result = `${result} ${i18n.t('calendar.alarm.before')}`
      } else {
        result = `${result} ${i18n.t('calendar.alarm.after')}`
      }

      return result
    })
  }

  get myMeetingResponse(): AttendeePartStat | null {
    return this.mMyMeetingResponse
  }

  // 내가 주최자인 회의는 응답할 대상이 아니므로 제외한다 (외부 생성 일정은 주최자 PARTSTAT 이 비어 있을 수 있음)
  get needsMyResponse(): boolean {
    return this.isMeeting && !this.mIsMyEvent && this.mMyMeetingResponse === null
  }

  get isDeclined(): boolean {
    return this.mMyMeetingResponse === 'DECLINED'
  }

  get isRSVP(): boolean {
    return this.mIsRSVP
  }

  get isMyEvent(): boolean {
    return this.mIsMyEvent
  }

  get isContainMe(): boolean {
    return this.mIsContainMe
  }

  get isCanceled(): boolean {
    return this.mIsCancelled
  }

  get isRecurring(): boolean {
    if (this.todo) {
      return this.todo.recurrenceRule != null
    }

    return this.event.rrule != null
  }

  get recurrenceRule(): RRule {
    if (this.todo) {
      return this.todo.recurrenceRule
    }

    return this.event.rrule as unknown as RRule
  }

  get hasException(): boolean {
    return !!this.exception
  }

  get attachments(): any[] | null {
    return this.mAttachments
  }

  public reload() {
    this.parseProperties()
  }
}

function paramToString(value: ParameterValue | undefined): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.val
}

export default EventInstance
